import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, RequireAuth, useAuth, rank } from "./lib/auth";
import Landing from "./pages/Landing";
import GuestMenu from "./pages/guest/Menu";
import OrderStatus from "./pages/guest/OrderStatus";
import MockPay from "./pages/MockPay";
import { ThemeControl } from "./components/ThemeControl";
import { PlanFeature } from "./components/PlanFeature";
const StaffLogin = lazy(() => import("./pages/staff/Login"));
const StaffLayout = lazy(() => import("./pages/staff/StaffLayout"));
const Orders = lazy(() => import("./pages/staff/Orders"));
const StaffProfile = lazy(() => import("./pages/staff/Profile"));
const Kitchen = lazy(() => import("./pages/staff/Kitchen"));
const Bills = lazy(() => import("./pages/staff/Bills"));
const Floor = lazy(() => import("./pages/staff/Floor"));
const Closing = lazy(() => import("./pages/staff/Closing"));
const PlatformLogin = lazy(() => import("./pages/platform/Login"));
const PlatformLayout = lazy(() => import("./pages/platform/Layout"));
const PlatformTenants = lazy(() => import("./pages/platform/Tenants"));
const CustomerAccount = lazy(() => import('./pages/guest/CustomerAccount'));
const PlatformAnalytics = lazy(() => import("./pages/platform/Analytics"));
const PlatformAnnouncements = lazy(() => import("./pages/platform/Announcements"));
const PlatformPlans = lazy(() => import("./pages/platform/Plans"));
const PlatformDiscounts = lazy(() => import("./pages/platform/Discounts"));
const PlatformProfile = lazy(() => import("./pages/platform/Profile"));
const PlatformTeam = lazy(() => import("./pages/platform/Team"));
const PlatformTenant = lazy(() => import("./pages/platform/TenantDetail"));
const PlatformHealth = lazy(() => import("./pages/platform/Ops").then((m) => ({ default: m.Health })));
const PlatformBilling = lazy(() => import("./pages/platform/Ops").then((m) => ({ default: m.Billing })));
const PlatformAccept = lazy(() => import("./pages/platform/Login").then((m) => ({ default: m.AcceptInvite })));
const PlatformActivity = lazy(() => import("./pages/platform/Team").then((m) => ({ default: m.Activity })));
const Signup = lazy(() => import("./pages/Auth").then((m) => ({ default: m.Signup })));
const Forgot = lazy(() => import("./pages/Auth").then((m) => ({ default: m.Forgot })));
const Reset = lazy(() => import("./pages/Auth").then((m) => ({ default: m.Reset })));
const Rewards = lazy(() => import("./pages/admin/Extras").then((m) => ({ default: m.Rewards })));
const Loyalty = lazy(() => import("./pages/admin/Extras").then((m) => ({ default: m.Loyalty })));
const DailySummary = lazy(() => import("./pages/admin/Extras").then((m) => ({ default: m.DailySummary })));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const Tables = lazy(() => import("./pages/admin/Tables"));
const MenuEditor = lazy(() => import("./pages/admin/MenuEditor"));
const Coupons = lazy(() => import('./pages/admin/Coupons'));
const Staff = lazy(() => import("./pages/admin/Staff"));
const Reports = lazy(() => import("./pages/admin/Reports"));
const OwnerInsights = lazy(() => import('./pages/admin/OwnerInsights'));
const OrderHistory = lazy(() => import('./pages/admin/OrderHistory'));
const StaffCreateOrder = lazy(() => import('./pages/staff/CreateOrderPage'));
const Outlets = lazy(() => import("./pages/admin/Settings").then((m) => ({ default: m.Outlets })));
const Tax = lazy(() => import("./pages/admin/Settings").then((m) => ({ default: m.Tax })));
const Subscription = lazy(() => import("./pages/admin/Settings").then((m) => ({ default: m.Subscription })));
const Audit = lazy(() => import("./pages/admin/Settings").then((m) => ({ default: m.Audit })));
const System = lazy(() => import("./pages/admin/Settings").then((m) => ({ default: m.System })));

function StaffHome() {
  const { outlet, user } = useAuth();
  if (user?.is_platform_admin) return <Navigate to="/platform" replace />;
  if (outlet?.my_role === 'cashier') return <Navigate to="/staff/bills" replace />;
  return <Navigate to={rank(outlet?.my_role) >= 2 ? "/staff/orders" : "/staff/kitchen"} replace />;
}

function NotFound() {
  return (
    <div className="grid min-h-screen place-items-center bg-[#FAF6F0] p-8 text-center" data-testid="not-found-page">
      <div>
        <p className="font-mono text-sm text-[#FF9D05]">404</p>
        <h1 className="mt-2 text-4xl font-extrabold">This plate is empty</h1>
        <p className="mt-2 text-stone-600">The page you're looking for doesn't exist.</p>
        <Link to="/" className="mt-6 inline-block rounded-full bg-stone-900 px-6 py-3 text-sm font-semibold text-white" data-testid="not-found-home">Go home</Link>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeControl />
      <AuthProvider>
        <Suspense fallback={<div className="grid min-h-screen place-items-center text-sm text-slate-400" data-testid="route-loading">Loading…</div>}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/m/:qr" element={<GuestMenu />} />
          <Route path="/customer" element={<CustomerAccount />} />
          <Route path="/order/:id" element={<OrderStatus />} />
          <Route path="/pay/mock/:gid" element={<MockPay />} />
          <Route path="/staff/login" element={<StaffLogin />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<Forgot />} />
          <Route path="/reset-password" element={<Reset />} />
          <Route path="/platform/login" element={<PlatformLogin />} />
          <Route path="/platform/accept-invite" element={<PlatformAccept />} />
          <Route path="/platform" element={<PlatformLayout />}>
            <Route index element={<PlatformTenants />} />
            <Route path="analytics" element={<PlatformAnalytics />} />
            <Route path="tenants/:tid" element={<PlatformTenant />} />
            <Route path="health" element={<PlatformHealth />} />
            <Route path="billing" element={<PlatformBilling />} />
            <Route path="announcements" element={<PlatformAnnouncements />} />
            <Route path="plans" element={<PlatformPlans />} />
            <Route path="discounts" element={<PlatformDiscounts />} />
            <Route path="profile" element={<PlatformProfile />} />
            <Route path="team" element={<PlatformTeam />} />
            <Route path="activity" element={<PlatformActivity />} />
          </Route>
          <Route path="/staff" element={<RequireAuth><StaffLayout /></RequireAuth>}>
            <Route index element={<StaffHome />} />
            <Route path="orders" element={<RequireAuth excludeRoles={['cashier']}><Orders /></RequireAuth>} />
            <Route path="profile" element={<StaffProfile />} />
            <Route path="create-order" element={<RequireAuth minRole="manager"><StaffCreateOrder /></RequireAuth>} />
            <Route path="kitchen" element={<RequireAuth excludeRoles={['cashier']}><PlanFeature feature="kitchen"><Kitchen /></PlanFeature></RequireAuth>} />
            <Route path="bills" element={<Bills />} />
            <Route path="floor" element={<RequireAuth excludeRoles={['cashier']}><Floor /></RequireAuth>} />
            <Route path="closing" element={<Closing />} />
          </Route>
          <Route path="/admin" element={<RequireAuth minRole="manager"><AdminLayout /></RequireAuth>}>
            <Route index element={<Navigate to="/admin/reports" replace />} />
            <Route path="reports" element={<Reports />} />
            <Route path="customers" element={<OwnerInsights customers />} />
            <Route path="order-history" element={<OrderHistory />} />
            <Route path="create-order" element={<StaffCreateOrder />} />
            <Route path="analytics" element={<OwnerInsights />} />
            <Route path="bills" element={<Bills />} />
            <Route path="profile" element={<StaffProfile />} />
            <Route path="menu" element={<MenuEditor />} />
            <Route path="coupons" element={<Coupons />} />
            <Route path="tables" element={<Tables />} />
            <Route path="staff" element={<PlanFeature feature="staff"><Staff /></PlanFeature>} />
            <Route path="outlets" element={<Outlets />} />
            <Route path="tax" element={<Tax />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="audit" element={<Audit />} />
            <Route path="system" element={<System />} />
            <Route path="inventory" element={<Navigate to="/admin/reports" replace />} />
            <Route path="loyalty" element={<Loyalty />} />
            <Route path="integrations" element={<section className="rounded-xl border bg-white p-6"><h1 className="text-2xl font-bold">Integrations disabled</h1><p className="mt-3 text-sm text-slate-500">Integrations are currently unavailable.</p></section>} />
            <Route path="summary" element={<DailySummary />} />
            <Route path="rewards" element={<Rewards />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <Toaster position="top-center" richColors expand visibleToasts={5} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
